import { Injectable, NgZone, computed, inject, signal } from '@angular/core';
import { Observable } from 'rxjs';
import { toObservable } from '@angular/core/rxjs-interop';

/**
 * The global busy state, ref-counted.
 *
 * `ui-busy-overlay` renders whatever this says. The counter lives HERE rather
 * than in `loading.interceptor.ts` (where it used to be a module-level `let`)
 * so a component that wants to block the screen for a non-HTTP task can join
 * the same count instead of racing the interceptor.
 *
 * Ref-counting matters: two concurrent mutations must not make the overlay
 * flicker, and the second one finishing must not hide an overlay the first one
 * still needs. `show()` on the 0 -> 1 transition, `hide()` only back at 0.
 *
 * `isLoading$` is preserved as the public surface both dashboards already use.
 */
@Injectable({ providedIn: 'root' })
export class LoaderService {
  private readonly zone = inject(NgZone);
  private readonly count = signal(0);

  readonly isLoading = computed(() => this.count() > 0);
  readonly isLoading$: Observable<boolean> = toObservable(this.isLoading);

  show(): void {
    this.zone.run(() => this.count.update((n) => n + 1));
  }

  hide(): void {
    this.zone.run(() => this.count.update((n) => Math.max(0, n - 1)));
  }

  /** Drop the count to zero. For a route change that abandons in-flight work. */
  reset(): void {
    this.zone.run(() => this.count.set(0));
  }
}
